import { defineStore } from 'pinia';
import { ref, readonly, computed } from 'vue';
import { apiGetAuthUser, UserDto } from '@/api/auth';
import { AccountDto, AccountType } from '@/api/account';
import { OAuthCredential } from 'firebase/auth';
import { firebaseAuth } from '@/config/firebase';

export const useAuthUserStore = defineStore('authUser', () => {
  const authUser = ref<null | UserDto>(null);
  const authAccount = ref<null | AccountDto>(null);
  const credentialToLinkOnLogin = ref<null | OAuthCredential>(null);

  const isAdmin = computed(() => authAccount.value?.type === AccountType.ADMIN);

  const setCredentialToLinkOnLogin = (data: OAuthCredential | null): void => {
    credentialToLinkOnLogin.value = data;
  };

  const setAuthUser = (data: UserDto): void => {
    authUser.value = data;
  };
  const clearAuthUser = (): void => {
    authUser.value = null;
    setCredentialToLinkOnLogin(null);
  };

  const setAuthAccount = (data: AccountDto): void => {
    authAccount.value = data;
  };
  const clearAuthAccount = (): void => {
    authAccount.value = null;
  };

  let isInitialized = false;
  const initializeAuthUser = async (): Promise<void> => {
    if (isInitialized) {
      return;
    }

    // Auth state is now driven by Firebase: wait for the persisted user to be
    // restored, then load the account if signed in (the API client attaches a
    // fresh ID token from currentUser).
    await firebaseAuth.authStateReady();

    if (!firebaseAuth.currentUser) {
      isInitialized = true;
      return;
    }

    try {
      const res = await apiGetAuthUser();

      setAuthUser(res);
    } catch {
      // Not authenticated / token rejected — leave authUser null.
    }

    isInitialized = true;
  };

  return {
    authUser: readonly(authUser),
    isAdmin,
    credentialToLinkOnLogin,
    setCredentialToLinkOnLogin,
    setAuthUser,
    clearAuthUser,
    setAuthAccount,
    clearAuthAccount,
    initializeAuthUser,
  };
});
